import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  sidebarFooterRecipe,
  sidebarFrameRecipe,
  sidebarPagesRecipe,
  sidebarToolbarRecipe,
} from '../recipes/nav.recipe';
import { BrandMark } from './BrandMark';

export interface SidebarFrameProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Along the top: the history arrows, and the collapse toggle at the right. */
  toolbar?: React.ReactNode;
  /** Under the toolbar: the company whose dashboard this is. */
  identity?: React.ReactNode;
  /** The pages: the rows of NavItems. */
  children?: React.ReactNode;
  /** Pinned to the bottom, under the Relay wordmark: the person. */
  footer?: React.ReactNode;
  /**
   * The icons-only sidebar, 64 wide: the same slots at the same heights, with
   * no foot. It is the same frame so that opening it moves nothing: the rail
   * was a layout of its own, and every row jumped under the pointer.
   */
  rail?: boolean;
}

/**
 * The dashboard's sidebar: the toolbar, the company, the pages under it, and
 * the foot with the Relay wordmark. The company takes the top, where Figma
 * 17:2777 has the wordmark, so the dashboard reads as the company's own. The
 * app fills the slots and decides what each holds; this decides where they
 * go.
 *
 *   <SidebarFrame
 *     toolbar={<><HistoryArrows /><CollapseToggle /></>}
 *     identity={<SidebarCompanySelectorView … />}
 *     footer={<SidebarUserProfileView … />}
 *   >
 *     {links.map((link) => <NavItem … />)}
 *   </SidebarFrame>
 */
export const SidebarFrame = React.forwardRef<HTMLDivElement, SidebarFrameProps>(
  function SidebarFrame(
    { toolbar, identity, footer, rail, children, className, ...props },
    ref
  ) {
    return (
      <div ref={ref} className={cn(sidebarFrameRecipe(), className)} {...props}>
        <div className={sidebarPagesRecipe()}>
          <div className={sidebarToolbarRecipe()}>{toolbar}</div>
          {identity}
          {children}
        </div>
        {rail ? null : (
          <div className={sidebarFooterRecipe()}>
            {/* Left: the column stretches its children, and a stretched image
                centres its art. */}
            <BrandMark
              name="relay"
              form="wordmark"
              size="sm"
              alt="Relay"
              className="self-start"
            />
            {footer}
          </div>
        )}
      </div>
    );
  }
);
